import React, { createRef, FocusEvent, KeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode, RefObject, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { bemClassNames, invariant, Key, TAB_INDEX_FOCUSABLE_ONLY, usePopupFlip } from "@multiweb/reactUtils";
import { Option } from "@multiweb/types/react";
import { MODAL_ROOT_ID } from "../../constants";
import { FocusProvider } from "../FocusProvider";

/** Hodnoty předávané části rozevíracího seznamu reprezentující vybranou položku */
export type SelectedOptionContentProps = {
    /** Příznak, zda je seznam otevřený */
    opened: boolean;
    /** Funkce pro otevírání/zavírání seznamu */
    setOpened: (opened: boolean) => void;
    /** Text vybrané položky */
    text: string | undefined;
};

/** Hodnoty předávané části rozevíracího seznamu reprezentující položky pro výběr */
export type OptionsContentProps<T> = {
    /** Příznak, zda je seznam otevřený */
    opened: boolean;
    /** Hodnota právě vybrané položky */
    value: T | undefined;
    /** Funkce volaná po výběru hodnoty */
    onSelect: (value: T) => void;
    /** Funkce předává objekt s referenci první a poslední položce seznamu. */
    assignRef: (index: number, optionsLength: number) => RefObject<HTMLButtonElement> | undefined;
};

export type SelectProps<T> = {
    /** Hodnota vybrané položky */
    value: T | undefined;
    /** Položky pro výběr zobrazené v seznamu */
    options: ReadonlyArray<Option<T>>;
    /** Obsah části rozevíracího seznamu reprezentující vybranou položku */
    selectedOptionContent: (props: SelectedOptionContentProps) => ReactNode;
    /** Obsah části rozevíracího seznamu reprezentující položky pro výběr */
    optionsContent: (props: OptionsContentProps<T>) => ReactNode;
    /** Funkce volaná po výběru hodnoty */
    onChange: (value?: T) => void;
};

const classNames = bemClassNames("select-component");

/** Komponenta rozevíracího seznamu */
export const Select = <T,>({
    value,
    options,
    selectedOptionContent,
    optionsContent,
    onChange,
}: SelectProps<T>) => {
    const modalRoot = document.getElementById(MODAL_ROOT_ID);
    invariant(modalRoot, `[Select] Cannot render select body because element with id ${MODAL_ROOT_ID} is undefined.`);
    const [opened, setOpened] = useState(false);
    const selectedOption = options.find((option) => option.value === value);
    const text = selectedOption?.text;
    const selectRef = useRef<HTMLDivElement>(null);
    const selectedOptionContentRef = useRef<HTMLDivElement>(null);
    const optionsContentRef = useRef<HTMLDivElement>(null);
    const { topOffset, leftOffset } = usePopupFlip({ triggerRef: selectedOptionContentRef, popupRef: optionsContentRef, opened, offset: 2 });
    const firstOptionRef = createRef<HTMLButtonElement>();
    const lastOptionRef = createRef<HTMLButtonElement>();

    /**
     * Volá `onChange` a přesouvá zaměření na Select.
     * @param value Vybraná hodnota.
     */
    const onSelect = (value: T) => {
        onChange(value);
        selectRef.current?.focus();
        setOpened(false);
    };

    /**
     * Funkce předá objekt s referencí první nebo poslední položce rozevíracího seznamu na základě indexu.
     * @param index Index položky.
     * @param optionsLength Počet položek.
     */
    const assignRef = (index: number, optionsLength: number) => {
        if (index === 0) {
            return firstOptionRef;
        }
        if (index === optionsLength - 1) {
            return lastOptionRef;
        }
        return undefined;
    };

    const handleBlur = (event: FocusEvent) => {
        if (event.relatedTarget instanceof Element) {
            // Zaměření se přesouvá v rámci rozevírací části, Select se nezavře.
            if (optionsContentRef.current?.contains(event.relatedTarget)) {
                return;
            }
        }
        setOpened(false);
    };

    const handleMouseDown = (event: ReactMouseEvent) => {
        // V Safari se rozevírací část zavře dříve než se zavolá onClick handler. Je to kvůli ztrátě focusu při mouseDown.
        event.preventDefault();
    };

    /**
     * Handler zachytí stisknutí kláves TAB nebo SHIFT + TAB a podle toho
     * přesouvá focus mezi rozvírací částí a tlačítkem pro výběr položky.
     * Je nutné manuální přesouvání focusu protože tab sequence se rozpojí při použití React Portal.
     * @param event
     */
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === Key.Tab && opened && event.target instanceof Element) {
            // První položka se zaměří pouze v případě, že je událost vyvolána mimo rozevírací část.
            if (!optionsContentRef.current?.contains(event.target)) {
                event.preventDefault();
                firstOptionRef.current?.focus();
                return;
            }

            // Na první položce byly stisknuty klávesy SHIFT + TAB, focus se vrátí na tlačítko pro výběr položky.
            const firstItemBlur = event.shiftKey && firstOptionRef.current?.contains(event.target);
            // Na poslední položce byla stisknuta klávesa TAB, focus se vrátí na tlačítko pro výběr položky.
            const lastItemBlur = !event.shiftKey && lastOptionRef.current?.contains(event.target);
            if (firstItemBlur || lastItemBlur) {
                event.preventDefault();
                selectRef.current?.focus();
                setOpened(false);
            }
        }
    };
    return (
        <FocusProvider onBlur={handleBlur} tabIndex={TAB_INDEX_FOCUSABLE_ONLY} >
            {() => (
                <div ref={selectRef} className={classNames()} onKeyDown={handleKeyDown} tabIndex={TAB_INDEX_FOCUSABLE_ONLY} >
                    <div
                        ref={selectedOptionContentRef}
                        className={classNames("selected-option-content")}
                    >
                        {selectedOptionContent({ opened, setOpened, text })}
                    </div>
                    {createPortal(
                        opened && (
                            <div
                                className={classNames("options-content")}
                                ref={optionsContentRef}
                                style={{ top: topOffset, left: leftOffset }}
                                onMouseDown={handleMouseDown}
                            >
                                {optionsContent({ value, opened, onSelect, assignRef })}
                            </div>
                        ),
                        modalRoot,
                    )}
                </div>
            )}
        </FocusProvider>
    );
};
